import { Action, Module, Mutation, VuexModule } from 'vuex-class-modules'
import {
  ApiResult,
  DeleteVehiclePhotoPayload,
  VehicleAmenityDTO,
  VehicleDetailEntity,
  VehiclePhotoDTO,
} from '@/models/dto'

import { RawLocation } from 'vue-router'
import app from './app'
import auth from './auth'
import deepClone from '@/utils/deepClone'
import { removeItem } from '@/utils/array'
import router from '@/router'
// register module (could be in any file)
import store from '@/store/index'
import vehicle from '@/services/vehicle'
import company from '@/store/modules/company'
import { GenericApiResult } from '@/models/dto/GenericApiResult'
import { AxiosResponse } from 'axios'
import { filter } from '@/utils/filter'
import { buildPhotoUploadErrorMessage } from '@/utils/photoUpload'
import {
  AmenityClassificationTypeId,
  AmenityTypeId,
  BestEffortAmenityTypeIds,
  VehicleAmenityInactiveReasonType,
  EndpointValidationStatus,
  VINReviewTypeKey,
  VINReviewStatusKey,
} from '@/utils/enum'
import type from '@/services/type'
import split from '@/store/modules/split'
import dayjs from 'dayjs'

const NEW_VEHICLE: VehicleDetailEntity = {
  active: false,
  companyId: null,
  deviceId: null,
  garageId: null,
  garageName: '',
  licensePlate: '',
  passengerCapacity: null,
  showOnOperatorPage: false,
  vehicleAmenityDTOs: [],
  vehicleId: null,
  vehicleMake: '',
  vehicleModel: '',
  vehicleName: '',
  vehiclePhotoDTOs: [],
  vehicleTypeId: null,
  vehicleTypeName: '',
  vehicleYear: null,
  vinNumber: '',
  updatedOn: '',
  decalTypeId: null,
  color: '',
}

@Module({ generateMutationSetters: true })
class VehicleDetailModule extends VuexModule {
  _routeName: string | null = null
  _isModeAdd = false
  _isModeEdit = false
  _isModeView = false
  _uploadPercentage = 0
  _vehicleId: number | null = null
  _vehicle: VehicleDetailEntity = NEW_VEHICLE
  _lastSavedVehicle: VehicleDetailEntity = NEW_VEHICLE
  _vehicles: VehicleDetailEntity[] = []
  _currentVehicleIndex: number = null
  _notFound = false
  _saving = false
  _loading = true
  _isVINValid: boolean = null
  _cleanVehicleAmenities: VehicleAmenityDTO[] = []
  _lastSavedOn: string | null = null
  _photoUploadError: string | null = null
  _photoRequirements: { maxSizeBytes: number; supportedMimeTypes: string[] } | null = null

  _vinValidationStatus: EndpointValidationStatus =
    EndpointValidationStatus.Inactive
  _lastValidatedVin: string = ''

  /**
   * Get the name of the route.
   * @returns the name of the route.
   */
  get routeName(): string | null {
    return this._routeName
  }
  /**
   * Check if the mode of the form is "add".
   * @returns `true` if the mode of the form is "add", `false` otherwise.*/
  get isModeAdd(): boolean {
    return this._isModeAdd
  }
  /**
   * Check if the mode of the form is "edit".
   * @returns `true` if the mode of the form is "edit", `false` otherwise.
   */
  get isModeEdit(): boolean {
    return this._isModeEdit
  }
  /**
   * Check if the mode of the form is "view".
   * @returns `true` if the mode of the form is "view", `false` otherwise.
   */
  get isModeView(): boolean {
    return this._isModeView
  }
  /**
   * Get the upload percentage for the vehicle photos.
   * @returns the upload percentage for the vehicle photos.
   */
  get uploadPercentage(): number {
    return this._uploadPercentage
  }
  /**
   * Get the details of the vehicle.
   * @returns the vehicle detail
   */
  get vehicle(): VehicleDetailEntity {
    return this._vehicle
  }
  /**
   * Get the ID of the vehicle.
   * @returns the vehicle ID.
   */
  get vehicleId(): number | null {
    return this._vehicleId
  }
  /**
   * Gets the list of vehicles.
   * @returns the list of vehicles
   */
  get vehicles(): VehicleDetailEntity[] {
    return this._vehicles
  }
  /**
   * Get the current vehicle index.
   * @returns the current vehicle index.
   */
  get currentVehicleIndex(): number | null {
    return this._currentVehicleIndex
  }
  /**
   * Get the list of photos for the vehicle.
   * @returns the list of photos for the vehicle.
   */
  get vehiclePhotos(): VehiclePhotoDTO[] {
    return this._vehicle?.vehiclePhotoDTOs || []
  }
  /**
   * Check if the vehicle is not found.
   * @returns `true` if the vehicle is not found
   */
  get notFound(): boolean {
    return this._notFound
  }
  /**
   * Check if the form is currently saving.
   * @returns whether the form is currently saving.
   */
  get saving(): boolean {
    return this._saving
  }
  /**
   * Check if the form is currently loading.
   * @returns whether the form is currently loading
   */
  get showLoaders(): boolean {
    return this._loading
  }

  /**
   * Get the last route before navigating to the vehicle edit page.
   * If the last route is the vehicle edit page or not found, the default route is "vehicles".
   * @returns the last route before navigating to the vehicle detail page.
   */
  get getLastRoute(): RawLocation {
    const lastRoute = app.lastRoute
    if (!app.lastRoute?.name || app.lastRoute?.name === 'vehicles.edit') {
      return { name: 'vehicles' }
    }
    return lastRoute
  }

  get isVINValid(): boolean {
    return this._isVINValid
  }

  /**
   * Get the VIN validation status.
   * @returns The current VIN validation status.
   */
  get vinValidationStatus(): EndpointValidationStatus {
    return this._vinValidationStatus
  }

  /**
   * Get the timestamp when the vehicle was last saved.
   * @returns The last saved timestamp, or null if not saved.
   */
  get lastSavedOn(): string | null {
    return this._lastSavedOn
  }

  /**
   * Get the photo upload error message.
   * @returns The error message, or null if no error.
   */
  get photoUploadError(): string | null {
    return this._photoUploadError
  }

  /**
   * Get the photo upload requirements.
   * @returns The requirements including max size and supported mime types.
   */
  get photoRequirements(): { maxSizeBytes: number; supportedMimeTypes: string[] } | null {
    return this._photoRequirements
  }

  /**
   * Check if make, model, or year have been edited.
   * @returns true if any of make, model, or year have changed from original values.
   */
  get isMakeModelYearEdited(): boolean {
    return (
      this._vehicle.vehicleMake !== this._lastSavedVehicle.vehicleMake ||
      this._vehicle.vehicleModel !== this._lastSavedVehicle.vehicleModel ||
      this._vehicle.vehicleYear !== this._lastSavedVehicle.vehicleYear
    )
  }

  /**
   * Get the last VIN that was validated.
   * @returns The last validated VIN.
   */
  get lastValidatedVin(): string {
    return this._lastValidatedVin
  }

  /**
   * Check if there are any unsaved changes to the vehicle.
   * @returns true if there are unsaved changes, false otherwise.
   */
  get hasUnsavedChanges(): boolean {
    const fieldsToCompare = new Set([
      'vehicleName',
      'vehicleTypeId',
      'passengerCapacity',
      'licensePlate',
      'garageId',
      'decalTypeId',
      'color',
    ])

    return (
      Object.keys(this._vehicle).some(
        (field) =>
          fieldsToCompare.has(field) &&
          !this.areFieldsEqual(
            this._vehicle[field],
            this._lastSavedVehicle[field]
          )
      ) ||
      !this.areAmenitiesEqual(
        this._vehicle.vehicleAmenityDTOs,
        this._lastSavedVehicle.vehicleAmenityDTOs
      ) ||
      !this.arePhotosEqual(
        this._vehicle.vehiclePhotoDTOs,
        this._lastSavedVehicle.vehiclePhotoDTOs
      ) ||
      this.isMakeModelYearEdited ||
      (this.isVINEdited && this._isVINValid)
    )
  }

  get isVINEdited(): boolean {
    return this._lastSavedVehicle.vinNumber !== this._vehicle.vinNumber
  }

  get isInformationComplete(): boolean {
    return (
      !!this._vehicle.vinNumber &&
      ((this.isVINEdited && this._isVINValid) ||
        (!this.isVINEdited &&
          this.vinValidationStatus !== EndpointValidationStatus.Failure)) &&
      !!this._vehicle.vehicleYear &&
      !!this._vehicle.vehicleMake &&
      !!this._vehicle.vehicleModel &&
      !!this._vehicle.vehicleName &&
      !!this._vehicle.vehicleTypeId &&
      !!this._vehicle.passengerCapacity &&
      !!this._vehicle.licensePlate &&
      !!this._vehicle.garageId &&
      !!this._vehicle.decalTypeId &&
      !!this._vehicle.color
    )
  }

  // Constants for validation
  ADA_CAPACITY_MIN = 1
  ADA_CAPACITY_MAX = 999
  ACCESS_METHOD_MIN_LENGTH = 3

  // ==================== Private Helper Methods ====================

  /**
   * Compares two amenity arrays for equality.
   * @param amenities1 - First amenity array to compare.
   * @param amenities2 - Second amenity array to compare.
   * @returns true if amenities are equal, false otherwise.
   */
  private areAmenitiesEqual(
    amenities1: VehicleAmenityDTO[],
    amenities2: VehicleAmenityDTO[]
  ): boolean {
    if (amenities1.length !== amenities2.length) {
      return false
    }
    return amenities1.every((amenity) => {
      const amenity2 = amenities2.find((a) => a.amenityId === amenity.amenityId)
      if (!amenity2) {
        return false
      }
      return (
        amenity.amenityId === amenity2.amenityId &&
        amenity.isSupported === amenity2.isSupported &&
        (amenity.inactiveReasonType?.key === amenity2.inactiveReasonType?.key ||
          (!amenity.inactiveReasonType && !amenity2.inactiveReasonType)) &&
        Object.keys(amenity.properties || {}).length ===
          Object.keys(amenity2.properties || {}).length &&
        Object.keys(amenity.properties || {}).every(
          (property) =>
            amenity.properties[property] === amenity2.properties[property]
        )
      )
    })
  }

  /**
   * Compares two photo arrays for equality.
   * @param photos1 - First photo array to compare.
   * @param photos2 - Second photo array to compare.
   * @returns true if photos are equal, false otherwise.
   */
  private arePhotosEqual(
    photos1: VehiclePhotoDTO[],
    photos2: VehiclePhotoDTO[]
  ): boolean {
    return photos1.every((photo) => {
      if (photo.vehiclePhotoId === null) {
        return !photo.active
      }
      const photo2 = photos2.find(
        (p) => p.vehiclePhotoId === photo.vehiclePhotoId
      )
      if (!photo2) {
        return false
      }
      return (
        photo.vehiclePhotoId === photo2.vehiclePhotoId &&
        photo.primaryImage === photo2.primaryImage &&
        photo.active === photo2.active &&
        photo.label === photo2.label
      )
    })
  }

  /**
   * Compares two field values for equality.
   * @param field1 - First field value to compare.
   * @param field2 - Second field value to compare.
   * @returns true if fields are equal, false otherwise.
   */
  private areFieldsEqual(
    field1: string | number,
    field2: string | number
  ): boolean {
    if (field1 === null || field1 === undefined) {
      return field2 === null || field2 === undefined
    }
    if (field2 === null || field2 === undefined) {
      return false
    }
    return String(field1) === String(field2)
  }

  private isValidADACapacity(capacity: string | undefined): boolean {
    if (!capacity) {
      return false
    }
    const numCapacity = Number(capacity)
    return (
      Number.isFinite(numCapacity) &&
      numCapacity >= this.ADA_CAPACITY_MIN &&
      numCapacity <= this.ADA_CAPACITY_MAX
    )
  }

  private isValidAccessMethod(accessMethod: string | undefined): boolean {
    return accessMethod?.trim().length >= this.ACCESS_METHOD_MIN_LENGTH
  }

  private hasBooleanProperty(
    properties: Record<string, string> | undefined,
    propertyName: string
  ): boolean {
    const value = properties?.[propertyName]
    return value === 'true' || value === 'false'
  }

  private isADAComplete(adaAmenity: VehicleAmenityDTO | undefined): boolean {
    if (!adaAmenity?.isSupported) {
      return true
    }
    return (
      this.isValidADACapacity(adaAmenity.properties?.['ada_capacity']) &&
      this.isValidAccessMethod(adaAmenity.properties?.['ada_access_method']) &&
      this.hasBooleanProperty(adaAmenity.properties, 'manual_backup_available')
    )
  }

  private isWiFiComplete(wifiAmenity: VehicleAmenityDTO | undefined): boolean {
    if (!wifiAmenity?.isSupported) {
      return true
    }
    return this.hasBooleanProperty(
      wifiAmenity.properties,
      'wifi_permanently_installed'
    )
  }

  get isFeaturesComplete(): boolean {
    const adaAmenity = this._vehicle.vehicleAmenityDTOs.find(
      (amenity) => amenity.amenityId === AmenityTypeId.ADACompliant
    )
    const wifiAmenity = this._vehicle.vehicleAmenityDTOs.find(
      (amenity) => amenity.amenityId === AmenityTypeId.Wifi
    )

    const allAmenitiesComplete = this._vehicle.vehicleAmenityDTOs.every(
      (amenity) =>
        amenity.isSupported ||
        (!amenity.isSupported &&
          amenity.inactiveReasonType &&
          amenity.inactiveReasonType.key !==
            VehicleAmenityInactiveReasonType.Unknown)
    )

    return (
      allAmenitiesComplete &&
      this.isADAComplete(adaAmenity) &&
      this.isWiFiComplete(wifiAmenity)
    )
  }

  // ==================== Mutations ====================

  /**
   * Set the status of the VIN validation.
   * @param vinValidationStatus - The status of the VIN validation.
   */
  @Mutation
  setVinValidationStatus(vinValidationStatus: EndpointValidationStatus) {
    this._vinValidationStatus = vinValidationStatus
  }

  /**
   * Set the last validated VIN.
   * @param lastValidatedVin - The last validated VIN.
   */
  @Mutation
  setLastValidatedVin(lastValidatedVin: string) {
    this._lastValidatedVin = lastValidatedVin
  }

  /**
   * Set whether the VIN is valid.
   * @param isVINValid - Whether the VIN is valid.
   */
  @Mutation
  setIsVINValid(isVINValid: boolean) {
    this._isVINValid = isVINValid
  }

  /**
   * Set the photo upload error message.
   * @param error - The error message, or null to clear.
   */
  @Mutation
  setPhotoUploadError(error: string | null) {
    this._photoUploadError = error
  }

  /**
   * Set the current vehicle index.
   * @param index - The current vehicle index.
   */
  @Mutation
  setCurrentVehicleIndex(index: number) {
    this._currentVehicleIndex = index
  }

  /**
   * Set the current vehicle device ID.
   * @param deviceId - The device ID.
   */
  @Mutation
  setDeviceId(deviceId: string) {
    this._vehicle.deviceId = deviceId
  }

  /**
   * Set the current vehicle ELD type.
   * @param eldType - The ELD type.
   */
  @Mutation
  setEldType(eldType: string) {
    this._vehicle.eldType = eldType
  }

  /**
   * Set the vehicle amenities.
   * @param vehicleAmenities - The list of vehicle amenities.
   */
  @Mutation
  setVehicleAmenities(vehicleAmenities: VehicleAmenityDTO[]) {
    if (this._currentVehicleIndex !== null) {
      this._vehicles[this._currentVehicleIndex].vehicleAmenityDTOs =
        vehicleAmenities
    } else {
      this._vehicle.vehicleAmenityDTOs = vehicleAmenities
    }
  }

  /**
   * Set the vehicle photos.
   * @param {VehiclePhotoDTO[]} vehiclePhotos - The list of vehicle photos.
   */
  @Mutation
  setVehiclePhotos(vehiclePhotos: VehiclePhotoDTO[]) {
    this._vehicle.vehiclePhotoDTOs = vehiclePhotos
  }

  /**
   * Set the label of a vehicle photo.
   * @param {VehiclePhotoDTO} photo - The vehicle photo to set the label for.
   */
  @Mutation
  setPhotoDTOLabel(photo: VehiclePhotoDTO) {
    let foundPhoto = this._vehicle.vehiclePhotoDTOs.find(
      (p) => p.imagePath === photo.imagePath
    )
    if (foundPhoto) {
      foundPhoto = photo
    }
  }

  /**
   * Set a vehicle photo as the primary photo.
   * @param {VehiclePhotoDTO} photo - The vehicle photo to set as primary.
   */
  @Mutation
  setPhotoDTOPrimaryImage(photo: VehiclePhotoDTO) {
    for (const photo of this._vehicle.vehiclePhotoDTOs) {
      photo.primaryImage = false
    }

    const foundPhoto = this._vehicle.vehiclePhotoDTOs.find(
      (p) => p.imagePath === photo.imagePath
    )
    if (foundPhoto) {
      foundPhoto.primaryImage = true
    }
  }

  // ==================== Actions ====================

  // --- Initialization Actions ---

  /**
   * Initialize the vehicle edit form.
   */
  @Action
  initialize(): void {
    this._vehicle = NEW_VEHICLE
    this._uploadPercentage = 0
    this._vehicleId = null
    this._vehicles = []
    this._currentVehicleIndex = null
    this._isVINValid = null
    this._vinValidationStatus = EndpointValidationStatus.Inactive
  }
  /**
   * Initialize the vehicle edit form with a vehicle entity.
   * @param vehicle - The vehicle entity.
   */
  @Action
  initializeVehicleSync(vehicles: VehicleDetailEntity[]): void {
    this._vehicle = NEW_VEHICLE
    this._uploadPercentage = 0
    this._vehicleId = null
    this._isModeAdd = true
    this._isModeEdit = false
    this._isModeView = false
    this._loading = false
    this._isVINValid = null
    this._vehicles = vehicles.map((vehicle) => {
      processVehicleAmenities(vehicle, this._cleanVehicleAmenities)
      return { ...deepClone(NEW_VEHICLE), ...vehicle }
    })
  }

  @Action
  resetVehicleDetail(): void {
    this._vehicleId = null
    this._vehicle = NEW_VEHICLE
    this._isVINValid = null
    this._vinValidationStatus = EndpointValidationStatus.Inactive
    this._lastValidatedVin = ''
    this._lastSavedVehicle = NEW_VEHICLE
    this._lastSavedOn = null
  }
  // --- Fetching Actions ---

  /**
   * Load the vehicle data for the form.
   */
  @Action
  async load(): Promise<void> {
    this._loading = true
    this._vehicles = []
    this._currentVehicleIndex = null
    this.setUploadPercentage(0)
    await Promise.all([company.getEldCredentials(), company.getVINReviews()])
    await Promise.all([
      this.fetchCleanVehicleAmenities(),
      this.fetchPhotoRequirements(),
    ])
    if (company.hasEldCredentials) {
      await company.getTrackingSources()
    }
    if (this._isModeAdd) {
      this.initialize()
      const vehicle = deepClone(this._vehicle)
      if (vehicle) {
        vehicle.active = true
        vehicle.companyId = auth.user?.companyId || null
        processVehicleAmenities(vehicle, this._cleanVehicleAmenities)
        this.setVehicle(vehicle)
      }
    } else {
      await this.fetchVehicle()
      processVehicleAmenities(this._vehicle, this._cleanVehicleAmenities)
    }
    this._lastSavedVehicle = deepClone(this._vehicle)
    this._loading = false
  }
  /**
   * Fetch the vehicle data from the server.
   */
  @Action
  async fetchVehicle(): Promise<void> {
    try {
      if (this._vehicleId) {
        const response = await vehicle.byId(Number(this._vehicleId))
        const vehicleData = this.processVehicle(response.data?.vehicle)
        const validateVINResult = await vehicle.validateVIN(
          vehicleData.vinNumber,
          this.vehicleId
        )
        this._lastValidatedVin = vehicleData.vinNumber
        if (validateVINResult.data.successful) {
          this.setVinValidationStatus(EndpointValidationStatus.Success)
          this.setIsVINValid(true)
        } else {
          this.setVinValidationStatus(EndpointValidationStatus.Failure)
          this.setIsVINValid(false)
        }
        this.setVehicle(vehicleData)
      } else {
        this._notFound = true
      }
    } catch (e) {
      this._notFound = true
      console.error(e)
    }
  }

  /**
   * Processes vehicle data and applies VIN review information if applicable.
   * @param vehicleDetail - The vehicle detail entity to process.
   * @param vinReview - Optional VIN review to apply to the vehicle.
   * @returns The processed vehicle detail entity.
   */
  private processVehicle(
    vehicleDetail: VehicleDetailEntity
  ): VehicleDetailEntity {
    if (vehicleDetail && typeof vehicleDetail.vehicleTypeId === 'string') {
      vehicleDetail.vehicleTypeId = parseInt(vehicleDetail.vehicleTypeId)
    }
    vehicleDetail.eldType = vehicleDetail.deviceEldTypeLabel
    vehicleDetail.vehicleAmenityDTOs = vehicleDetail.vehicleAmenityDTOs.sort(
      (a, b) => a.amenityId - b.amenityId
    )
    const vinReview = company.vinReviews?.find(
      (review) => review.vehicleId === vehicleDetail.vehicleId
    )
    if (
      vinReview &&
      vinReview.vinReviewType?.key === VINReviewTypeKey.Update &&
      (vinReview.vinReviewStatus?.key === VINReviewStatusKey.Pending ||
        vinReview.vinReviewStatus?.key === VINReviewStatusKey.Rejected)
    ) {
      vehicleDetail.vinNumber = vinReview.vinNumber
      vehicleDetail.vehicleMake = vinReview.vehicleMake
      vehicleDetail.vehicleModel = vinReview.vehicleModel
      vehicleDetail.vehicleYear = vinReview.vehicleYear
    }
    return vehicleDetail
  }
  /**
   * Fetches the vehicle's ELD device information.
   */
  @Action
  async fetchDeviceInformation(): Promise<void> {
    if (this._vehicle.vinNumber) {
      const response = await vehicle.getVehicleTrackingData([this._vehicle])
      const deviceInfo = response.data.data
      if (deviceInfo) {
        this.setDeviceId(deviceInfo[0]?.trackingDeviceId)
        this.setEldType(deviceInfo[0]?.eldType)
      }
    } else {
      this.setDeviceId('')
      this.setEldType('')
    }
  }

  /**
   * Fetches and filters the clean vehicle amenities from the server.
   *
   * Creates filters for vehicle amenity classification and alcohol amenity types,
   * and sends a request to retrieve amenities data. The result is filtered to exclude
   * best effort amenity types and is then mapped to a list of VehicleAmenityDTOs
   * with default support set to false.
   *
   * @returns A promise that resolves when the operation is complete.
   */
  @Action
  async fetchCleanVehicleAmenities(): Promise<void> {
    const filterUtil = filter()
    const parentFilter = filterUtil.createParent('or')
    const vehicleAmenityClassificationFilter = {
      column: {
        _t_id: '4fb73402-261a-4938-b610-b3c89d09e149',
        value: 'amenityClassificationTypeId',
        filterType: 'eq',
      },
      value: AmenityClassificationTypeId.Vehicle,
    }
    const alcoholAmenityFilter = {
      column: {
        _t_id: 'a9849013-d10a-4613-8a84-5302dacd7689',
        value: 'id',
        filterType: 'eq',
      },
      value: AmenityTypeId.AlcoholConsumption,
    }

    filterUtil.add(parentFilter, vehicleAmenityClassificationFilter)
    filterUtil.add(parentFilter, alcoholAmenityFilter)
    const isVehicleProfileUpdatesEnabled =
      await split.isVehicleAmenityUpdatesEnabled()
    if (isVehicleProfileUpdatesEnabled) {
      const spabAmenityFilter = {
        column: {
          _t_id: 'd4d60a1a-c591-11f0-8de9-0242ac120002',
          value: 'id',
          filterType: 'eq',
        },
        value: AmenityTypeId.SPABCompliant,
      }
      filterUtil.add(parentFilter, spabAmenityFilter)
    }

    try {
      const response = await type.amenity({
        pageSize: -1,
        filters: filterUtil.asQueryParams(),
      })
      let amenities = response?.data?.resultList || []

      amenities = amenities.filter(
        (amenity) =>
          !Object.values(BestEffortAmenityTypeIds).includes(amenity.id)
      )

      this._cleanVehicleAmenities = amenities.map((amenity) => {
        return {
          isSupported: false,
          amenityId: amenity.id,
          amenityName: amenity.label,
          inactiveReasonType: { key: VehicleAmenityInactiveReasonType.Unknown },
        }
      })
    } catch (e) {
      console.warn('Error getting vehicle amenities', e)
    }
  }

  /**
   * Fetches the photo upload requirements from the server.
   */
  @Action
  async fetchPhotoRequirements(): Promise<void> {
    try {
      const response = await vehicle.getPhotoRequirements()
      this._photoRequirements = response.data
    } catch (e) {
      console.warn('Error getting photo requirements', e)
    }
  }
  // --- Setting Actions ---

  /**
   * Sets the route name.
   * @param routeName - The name of the route.
   */
  @Action
  setRouteName(routeName: string): void {
    this._routeName = routeName
    this._isModeAdd = routeName === 'vehicles.add'
    if (this._isModeAdd) {
      this._loading = false
    }
    this._isModeEdit = routeName === 'vehicles.edit'
    this._isModeView = routeName === 'vehicles.view'
  }
  /**
   * Sets the upload percentage for a vehicle.
   * @param percent - The percentage of the upload that has completed.
   */
  @Action
  setUploadPercentage(percent: number) {
    this._uploadPercentage = percent
  }
  /**
   * Sets the details of a vehicle.
   * @param vehicle - The details of the vehicle.
   */
  @Action
  setVehicle(vehicle: VehicleDetailEntity) {
    if (this._currentVehicleIndex !== null) {
      this._vehicles[this._currentVehicleIndex] = vehicle
    } else {
      this._vehicle = vehicle
    }
  }
  /**
   * Sets the ID of a vehicle.
   * @param vehicleId - The ID of the vehicle.
   */
  @Action
  setVehicleId(vehicleId: number | string | null) {
    if (typeof vehicleId === 'string') {
      vehicleId = parseInt(vehicleId)
    }
    this._vehicleId = vehicleId
  }
  /**
   * Sets whether the vehicle is currently being saved.
   * @param saving - Whether the vehicle is currently being saved.
   */
  @Action
  setSaving(saving: boolean) {
    this._saving = saving
  }
  /**
   * Sets the amenities of a vehicle.
   * @param vehicleAmenities - The amenities of the vehicle.
   */
  @Action
  setAmenities(vehicleAmenities: VehicleAmenityDTO[]) {
    this.setVehicleAmenities(vehicleAmenities)
  }

  /**
   * Sets the photos of a vehicle.
   * @param vehiclePhotos - The photos of the vehicle.
   */
  @Action
  setPhotos(vehiclePhotos: VehiclePhotoDTO[]) {
    this.setVehiclePhotos(vehiclePhotos)
  }

  // --- Navigation Actions ---

  /**
   * Navigates the user to the previous route.
   */
  @Action
  goBack(): void {
    if (!app.lastRoute?.name || app.lastRoute?.name === 'vehicles.edit') {
      router.push({ name: 'vehicles' })
    } else {
      router.push(app.lastRoute)
    }
  }
  /**
   * Begins the process of editing a vehicle.
   */
  @Action
  beginEdit(): void {
    if (this._vehicleId) {
      router.push({
        name: 'vehicles.edit',
        params: { id: this._vehicleId.toString() },
      })
    }
  }
  /**
   * Sets the label for a vehicle photo.
   * @param obj - An object containing the photo and the label to set.
   */
  @Action
  setPhotoLabel(obj: { photo: VehiclePhotoDTO; label: string }): void {
    const photo = obj.photo
    photo.label = obj.label
    this.setPhotoDTOLabel(photo)
  }
  /**
   * Sets a photo as the primary photo for a vehicle.
   * @param photo - The photo to set as the primary photo.
   */
  @Action
  setPhotoAsPrimaryImage(photo: VehiclePhotoDTO): void {
    this.setPhotoDTOPrimaryImage(photo)
  }

  // --- Photo Management Actions ---

  /**
   * Uploads photos for a vehicle.
   */
  @Action
  async uploadPhotos(): Promise<void> {
    if (!this._vehicleId) {
      return
    }

    const currentVehicle = this._currentVehicleIndex
      ? this._vehicles[this._currentVehicleIndex]
      : this._vehicle

    const newVehiclePhotos = buildNewPhotosPayload(
      currentVehicle.vehiclePhotoDTOs
    )

    if (!newVehiclePhotos) {
      return
    }

    const results = await Promise.allSettled(
      newVehiclePhotos.map((form) =>
        vehicle.uploadPhotos(this._vehicleId, form)
      )
    )

    const failures = filterPhotoUploadFailures(results)

    if (failures.length > 0) {
      const errorDetails = failures[0].reason?.response?.data?.error?.details
      this.setPhotoUploadError(
        buildPhotoUploadError(errorDetails, failures.length)
      )
      throw failures[0].reason
    }

    this.setPhotoUploadError(null)
  }

  /**
   * Updates the default photo for a vehicle.
   */
  @Action
  async updateDefaultPhoto(): Promise<void> {
    if (!this._vehicleId) {
      return
    }
    const defaultPhoto = this._vehicle.vehiclePhotoDTOs.find(
      (p) => p.primaryImage
    )

    // If the defaultPhoto is a photo which still needs to be uploaded
    if (!defaultPhoto || defaultPhoto?.file || !defaultPhoto.active) {
      return
    }
    await vehicle.makeVehiclePhotoDefault(this._vehicleId, defaultPhoto)
  }

  /**
   * Adds photos to a vehicle.
   * @param photos - The photos to add to the vehicle.
   */
  @Action
  addPhotos(photos: VehiclePhotoDTO[]): void {
    let newPhotosList
    if (this._currentVehicleIndex !== null) {
      newPhotosList = this._vehicles[this._currentVehicleIndex].vehiclePhotoDTOs
    } else {
      newPhotosList = this._vehicle.vehiclePhotoDTOs
    }
    for (const photo of photos) {
      newPhotosList.push(photo)
    }
  }

  /**
   * Removes a photo from a vehicle.
   * @param photo - The photo to remove from the vehicle.
   */
  @Action
  removePhoto(photo: VehiclePhotoDTO): void {
    photo.active = false
    if (photo.file) {
      let vehiclePhotoList = deepClone(this.vehiclePhotos)
      vehiclePhotoList = removeItem(vehiclePhotoList, photo)
      this.setVehiclePhotos(vehiclePhotoList)
    }
  }

  // --- CRUD Actions ---

  /**
   * Updates the details of a vehicle using the provided method.
   * @param updateVehicleMethod - The method to use for updating the vehicle.
   * @param skipRedirect - Whether to skip redirecting after update.
   * @returns The API result of the update operation.
   */
  async updateVehicleUsingMethod(
    updateVehicleMethod: (
      vehicle: VehicleDetailEntity
    ) => Promise<AxiosResponse<ApiResult>>,
    skipRedirect = false
  ): Promise<ApiResult> {
    if (this._vehicleId) {
      try {
        const deletedPhotos = buildDeletePhotosPayload(this._vehicle)
        if (deletedPhotos.vehiclePhotoIds.length) {
          await vehicle.deleteVehiclePhotos(this._vehicleId, deletedPhotos)
        }
        const response = await updateVehicleMethod(this._vehicle)
        if (response.data.successful) {
          await this.updateDefaultPhoto()
          await this.uploadPhotos()
          this._lastSavedOn = dayjs().format('M/D/YY h:mm A')
          if (!skipRedirect) {
            router.push({
              name: 'vehicles.view',
              params: { id: this._vehicleId.toString() },
            })
          }
        }
        return response.data
      } catch (e) {
        console.error(e)
      }
    }
  }

  /**
   * Updates the details of a vehicle.
   */
  @Action
  async updateVehicle(skipRedirect = false): Promise<ApiResult> {
    return this.updateVehicleUsingMethod(vehicle.update, skipRedirect)
  }

  /**
   * Updates the details of a vehicle while withholding vin changes using a vin review
   */
  @Action
  async updateVehicleWithReview(skipRedirect = false): Promise<ApiResult> {
    return this.updateVehicleUsingMethod(vehicle.updateWithReview, skipRedirect)
  }

  /**
   * Adds a vehicle.
   * @param addVehicleMethod - The method to call to add a vehicle.
   * @param skipRedirect - Whether to skip redirecting to the new vehicle's page.
   */
  async addVehicleUsingMethod(
    addVehicleMethod: (
      vehicle: VehicleDetailEntity
    ) => Promise<AxiosResponse<GenericApiResult<number>>>,
    skipRedirect = false
  ): Promise<GenericApiResult<number>> {
    try {
      let currentVehicle
      if (this._currentVehicleIndex !== null) {
        currentVehicle = this._vehicles[this._currentVehicleIndex]
      } else {
        currentVehicle = this._vehicle
      }
      // If there are vehiclePhotoDTOs and no primary image
      // is set, set the first photo to be the primary image.
      if (currentVehicle.vehiclePhotoDTOs?.length) {
        if (
          !currentVehicle.vehiclePhotoDTOs.filter((p) => p.primaryImage).length
        ) {
          currentVehicle.vehiclePhotoDTOs[0].primaryImage = true
        }
      }

      const vehicleResponse = await addVehicleMethod(currentVehicle)
      const vehicleId = vehicleResponse.data.data
      this.setVehicleId(vehicleId)
      await this.uploadPhotos()
      if (!vehicleResponse.data.successful) {
        return vehicleResponse.data
      }
      this._lastSavedOn = dayjs().format('M/D/YY h:mm A')
      router.push({
        name: skipRedirect ? 'vehicles.edit' : 'vehicles.view',
        params: { id: vehicleId.toString() },
      })
      this.setVinValidationStatus(EndpointValidationStatus.Inactive)
      return vehicleResponse.data
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Adds a vehicle.
   * @param skipRedirect - Whether to skip redirecting to the new vehicle's page.
   */
  @Action
  async addVehicle(skipRedirect = false): Promise<GenericApiResult<number>> {
    return this.addVehicleUsingMethod(vehicle.create, skipRedirect)
  }

  /**
   * Adds a vehicle with a review.
   * @param skipRedirect - Whether to skip redirecting to the new vehicle's page.
   */
  @Action
  async addVehicleWithReview(
    skipRedirect = false
  ): Promise<GenericApiResult<number>> {
    return this.addVehicleUsingMethod(vehicle.createWithReview, skipRedirect)
  }
}

// ==================== Private Helper Functions ====================

const buildNewPhotosPayload = (
  vehiclePhotoDTOs: VehiclePhotoDTO[]
): FormData[] | null => {
  const forms = []
  for (const photo of vehiclePhotoDTOs) {
    if (photo?.file) {
      const form = new FormData()
      form.append('file', photo.file)
      form.append('label', photo.label)
      form.append('primaryImage', String(photo.primaryImage))
      forms.push(form)
    }
  }
  return forms.length ? forms : null
}

const buildDeletePhotosPayload = (
  vehicle: VehicleDetailEntity
): DeleteVehiclePhotoPayload => {
  const deletedPhotos = vehicle.vehiclePhotoDTOs
    .filter((photo) => !photo.active)
    .map((photo) => {
      return photo.vehiclePhotoId
    })
  return { vehiclePhotoIds: deletedPhotos }
}

/**
 * Maps a list of vehicle amenities into a vehicle's vehicleAmenityDTOs while
 * preserving any existing isSupported values.
 *
 * @param {VehicleDetailEntity} vehicle - The vehicle entity to update.
 * @param {VehicleAmenityDTO[]} amenities - The list of vehicle amenities to map.
 * @returns {void}
 */
const processVehicleAmenities = (
  vehicle: VehicleDetailEntity,
  amenities: VehicleAmenityDTO[]
) => {
  vehicle.vehicleAmenityDTOs = amenities.map((amenity) => {
    const existingAmenity = vehicle.vehicleAmenityDTOs.find(
      (a) => a.amenityId === amenity.amenityId
    )
    if (existingAmenity) {
      amenity.isSupported = existingAmenity.isSupported
      amenity.inactiveReasonType = existingAmenity.inactiveReasonType
      amenity.properties = existingAmenity.properties
    }
    return amenity
  })
}

const isPhotoUploadFailure = (result: PromiseSettledResult<unknown>): boolean =>
  result.status === 'rejected' &&
  (result.reason?.response?.status === 413 ||
    result.reason?.response?.status === 415)

const filterPhotoUploadFailures = (
  results: PromiseSettledResult<unknown>[]
): PromiseRejectedResult[] =>
  results.filter((result): result is PromiseRejectedResult =>
    isPhotoUploadFailure(result)
  )

const buildPhotoUploadError = (
  errorDetails:
    | { supportedTypes?: string[]; maxSizeBytes?: number }
    | undefined,
  failedCount: number
): string => {
  if (errorDetails?.supportedTypes && errorDetails?.maxSizeBytes) {
    return buildPhotoUploadErrorMessage(
      {
        supportedTypes: errorDetails.supportedTypes,
        maxSizeBytes: errorDetails.maxSizeBytes,
      },
      failedCount
    )
  }
  return failedCount === 1 ? 'File not supported.' : 'Files not supported.'
}

export default new VehicleDetailModule({ store, name: 'vehicleDetail' })
